import { useMutation, UseMutationResult } from 'react-query';

import { useAuth } from 'src/hooks';
import { useProgressStore } from 'src/hooks/useProgressStore';

import { settingsResetPassword } from 'src/utils/urls';

type ApiErrorItem = {
  Code: string;
  FieldName: string;
  Message: string;
};

type TResult = {
  Errors: ApiErrorItem[];
  ErrorId: string;
  IsSuccess: boolean;
  StatusCode: number;
  Data?: unknown;
};

type TVariables = {
  CurrentPassword: string;
  NewPassword: string;
  ConfirmPassword: string;
};

type TError = Error;
type TSnapshot = unknown;

async function resetPassword({
  token,
  tokenType,
  params,
}: {
  token: string;
  tokenType: string;
  params: TVariables;
}): Promise<TResult> {
  const response = await fetch(settingsResetPassword, {
    method: 'POST',
    headers: {
      Authorization: `${tokenType} ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      CurrentPassword: params.CurrentPassword,
      NewPassword: params.NewPassword,
      ConfirmNewPassword: params.ConfirmPassword,
    }),
  });

  let data: TResult | null = null;

  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    console.error('JSON parse failed:', e);
  }

  if (!data) {
    throw new Error('Invalid server response');
  }

  return data;
}

function useResetPassword(): UseMutationResult<
  TResult,
  TError,
  TVariables,
  TSnapshot
> {
  const { token, tokenType } = useAuth();
  const { setLoading } = useProgressStore();

  return useMutation(
    async (params: TVariables) => {
      setLoading('isResetPasswordLoading', true);

      const result = await resetPassword({
        token,
        tokenType,
        params,
      });

      if (!result.IsSuccess) {
        const errorMessage =
          result.Errors?.map(err => err.Message).join(', ') ||
          'Password reset failed';
        throw new Error(errorMessage);
      }

      return result;
    },
    {
      onSettled: () => {
        setLoading('isResetPasswordLoading', false);
      },
    },
  );
}

export { useResetPassword };
